import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import pickleballIcon from "./pickleball.png";
import AddressSearchModal from "./components/AddressSearchModal";

const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const CITIES = ["서울", "경기", "인천", "부산", "대구", "대전", "광주", "울산", "세종", "기타"];

const SEOUL_DISTRICTS = [
  "종로구","중구","용산구","성동구","광진구","동대문구","중랑구","성북구",
  "강북구","도봉구","노원구","은평구","서대문구","마포구","양천구","강서구",
  "구로구","금천구","영등포구","동작구","관악구","서초구","강남구","송파구","강동구",
];

const DESIRED_LEVELS = ["무관", "입문", "초급", "중급", "고급"];

const TODAY = new Date().toISOString().split("T")[0];

// editPost: null이면 작성 모드, 모집글 객체이면 수정 모드
export default function PostCreate({ onBack, onSaved, editPost = null }) {
  const { user } = useAuth();
  const isEditMode = !!editPost;

  // 팀장인 팀 목록 — 작성 모드에서만 사용
  const [leaderTeams,   setLeaderTeams]   = useState([]);
  const [leaderLoading, setLeaderLoading] = useState(!isEditMode);
  const [selectedTeam,  setSelectedTeam]  = useState(null);

  useEffect(() => {
    if (isEditMode || !user?.id) { setLeaderLoading(false); return; }
    supabase.from("teams").select("*").eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        const list = data || [];
        setLeaderTeams(list);
        if (list.length === 1) setSelectedTeam(list[0]);
        setLeaderLoading(false);
      });
  }, [isEditMode, user?.id]); // eslint-disable-line

  // 팀 선택 시 종목 자동 세팅
  useEffect(() => {
    if (!isEditMode && selectedTeam?.sports_type) setSportsType(selectedTeam.sports_type);
  }, [selectedTeam, isEditMode]); // eslint-disable-line

  const [gameDate,          setGameDate]          = useState(editPost?.game_date                              || "");
  const [locationCity,      setLocationCity]      = useState(editPost?.location_city                          || "서울");
  const [locationDistrict,  setLocationDistrict]  = useState(editPost?.location_district                      || "");
  const [locationDong,      setLocationDong]      = useState(editPost?.location_dong                          || "");
  const [locationName,      setLocationName]      = useState(editPost?.location_name                          || "");
  const [locationMapx,      setLocationMapx]      = useState(editPost?.location_mapx                          || null);
  const [locationMapy,      setLocationMapy]      = useState(editPost?.location_mapy                          || null);
  const [locationRoadAddr,  setLocationRoadAddr]  = useState(editPost?.location_road_address                  || "");
  const [showAddrModal,     setShowAddrModal]     = useState(false);
  const [desiredLevels,     setDesiredLevels]     = useState(() => {
    if (!editPost?.desired_level) return ["무관"];
    if (Array.isArray(editPost.desired_level)) return editPost.desired_level;
    try { const p = JSON.parse(editPost.desired_level); return Array.isArray(p) ? p : [editPost.desired_level]; } catch { return [editPost.desired_level]; }
  });
  const [gameTimeStart,     setGameTimeStart]     = useState(editPost?.game_time_start                        || "");
  const [gameTimeEnd,       setGameTimeEnd]       = useState(editPost?.game_time_end                          || "");
  const [participantMale,   setParticipantMale]   = useState(editPost?.participant_male   != null ? String(editPost.participant_male)   : "");
  const [participantFemale, setParticipantFemale] = useState(editPost?.participant_female != null ? String(editPost.participant_female) : "");
  const [message,           setMessage]           = useState(editPost?.message                                || "");
  const [deadline,          setDeadline]          = useState(editPost?.deadline                               || "");
  const [sportsType,        setSportsType]        = useState(editPost?.sports_type || "");
  const [loading,           setLoading]           = useState(false);
  const [error,             setError]             = useState("");
  const [validationModal,   setValidationModal]   = useState(null); // null | string[]

  const handleSubmit = async () => {
    if (!isEditMode && !selectedTeam) { setError("팀을 선택해주세요"); return; }

    // 필수사항 유효성 검사
    const missing = [];
    if (!sportsType) missing.push("종목");
    if (!gameDate)   missing.push("교류전 희망 날짜");
    if (missing.length > 0) {
      setValidationModal(missing);
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      game_date:          gameDate,
      location_city:      locationCity,
      location_district:  locationDistrict || null,
      location_dong:      locationDong     || null,
      location_name:      locationName     || null,
      location_mapx:      locationMapx     || null,
      location_mapy:      locationMapy     || null,
      location_road_address: locationRoadAddr || null,
      desired_level:      JSON.stringify(desiredLevels),
      game_time_start:    gameTimeStart || null,
      game_time_end:      gameTimeEnd   || null,
      participant_male:   participantMale   !== "" ? parseInt(participantMale,   10) : null,
      participant_female: participantFemale !== "" ? parseInt(participantFemale, 10) : null,
      message:            message.trim()   || null,
      deadline:           deadline         || null,
    };

    if (isEditMode) {
      // 수정 모드: updated_at을 현재 시간으로 갱신 → 최신글로 올라오게
      const { data: updated, error: updateErr } = await supabase
        .from("posts")
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq("id", editPost.id)
        .select()
        .single();
      setLoading(false);
      if (updateErr) {
        setError("수정 실패: " + updateErr.message);
      } else {
        onSaved?.(updated);
      }
    } else {
      // 작성 모드: 신규 등록
      const { error: insertErr } = await supabase.from("posts").insert([{
        ...payload,
        team_id:     selectedTeam.id,
        team_name:   selectedTeam.team_name,
        sports_type: sportsType,
        status:      "open",
      }]);
      setLoading(false);
      if (insertErr) {
        setError("모집글 등록 실패: " + insertErr.message);
      } else {
        onSaved?.();
      }
    }
  };

  // 작성 모드: 팀장 팀 로딩 중
  if (!isEditMode && leaderLoading) {
    return (
      <div style={s.container}>
        <Header onBack={onBack} title="모집글 작성" />
        <div style={s.emptyWrap}>
          <style>{`@keyframes pcSpin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", animation: "pcSpin 0.8s linear infinite" }} />
        </div>
      </div>
    );
  }

  // 작성 모드: 팀장인 팀 없음
  if (!isEditMode && leaderTeams.length === 0) {
    return (
      <div style={s.container}>
        <Header onBack={onBack} title="모집글 작성" />
        <div style={s.emptyWrap}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>📋</div>
          <div style={{ fontSize: 16, color: "#6b7280", fontWeight: 600 }}>팀장만 모집글을 작성할 수 있어요</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 6 }}>
            팀을 먼저 만들고 팀장이 되어주세요
          </div>
        </div>
      </div>
    );
  }

  const sport = SPORTS[sportsType] || { icon: "🏅", label: sportsType || "미선택" };

  const displayTeamName = isEditMode ? editPost.team_name : selectedTeam?.team_name;
  const displayLevel    = isEditMode ? editPost.level      : selectedTeam?.level;

  return (
    <div style={s.container}>
      <Header onBack={onBack} title={isEditMode ? "모집글 수정" : "모집글 작성"} />

      <div style={s.body}>
        {/* 팀장인 팀이 2개 이상: 상단에 팀 선택 */}
        {!isEditMode && leaderTeams.length >= 2 && (
          <div style={s.section}>
            <div style={s.sectionTitle}>어느 팀으로 작성할까요?</div>
            {leaderTeams.map(t => {
              const sp    = SPORTS[t.sports_type] || { icon: "🏅", label: t.sports_type };
              const isSel = selectedTeam?.id === t.id;
              return (
                <div
                  key={t.id}
                  style={{ ...s.teamBox, cursor: "pointer", marginBottom: 8, border: isSel ? "2px solid #E8412A" : "1px solid #e5e7eb", background: isSel ? "#fff8f8" : "#fff" }}
                  onClick={() => setSelectedTeam(t)}
                >
                  <span style={{ fontSize: 22 }}>{sp.icon || "🏅"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{t.team_name}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>{sp.label}{t.level ? ` · ${t.level}` : ""}</div>
                  </div>
                  {isSel && <span style={{ fontSize: 13, color: "#E8412A", fontWeight: 700 }}>선택됨 ✓</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* 팀 정보 */}
        <div style={s.section}>
          <div style={s.sectionTitle}>{isEditMode ? "모집 팀" : "신청 팀"}</div>
          <div style={s.teamBox}>
            <span style={{ fontSize: 22 }}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 22, height: 22, objectFit: "contain" }} />}</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{displayTeamName}</div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>{sport.label}{displayLevel ? ` · ${displayLevel}` : ""}</div>
            </div>
          </div>
        </div>

        {/* 종목 선택 */}
        <div style={s.section}>
          <label style={s.label}>
            종목 <span style={s.required}>*</span>
          </label>
          {selectedTeam?.sports_type && !isEditMode ? (
            <div style={{ ...s.teamBox, background: "#f9fafb" }}>
              <span style={{ fontSize: 20 }}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 20, height: 20, objectFit: "contain" }} />}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{sport.label}</span>
              <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>팀 종목</span>
            </div>
          ) : (
            <select
              style={s.input}
              value={sportsType}
              onChange={(e) => setSportsType(e.target.value)}
            >
              <option value="">종목을 선택하세요</option>
              {Object.entries(SPORTS).map(([key, val]) => (
                <option key={key} value={key}>{val.icon} {val.label}</option>
              ))}
            </select>
          )}
        </div>

        {/* 교류전 희망 날짜 */}
        <div style={s.section}>
          <label style={s.label}>
            교류전 희망 날짜 <span style={s.required}>*</span>
          </label>
          <input
            type="date"
            style={s.input}
            value={gameDate}
            min={isEditMode ? undefined : TODAY}
            onChange={(e) => setGameDate(e.target.value)}
          />
        </div>

        {/* 교류전 희망 시간 */}
        <div style={s.section}>
          <label style={s.label}>
            교류전 희망 시간 <span style={s.optional}>선택</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="time"
              style={{ ...s.input, flex: 1 }}
              value={gameTimeStart}
              onChange={(e) => setGameTimeStart(e.target.value)}
            />
            <span style={{ fontSize: 14, color: "#9ca3af", flexShrink: 0 }}>~</span>
            <input
              type="time"
              style={{ ...s.input, flex: 1 }}
              value={gameTimeEnd}
              onChange={(e) => setGameTimeEnd(e.target.value)}
            />
          </div>
        </div>

        {/* 장소 */}
        <div style={s.section}>
          <label style={s.label}>교류전 희망 장소</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <select
              style={{ ...s.input, flex: 1 }}
              value={locationCity}
              onChange={(e) => { setLocationCity(e.target.value); setLocationDistrict(""); }}
            >
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {locationCity === "서울" ? (
              <select
                style={{ ...s.input, flex: 1 }}
                value={locationDistrict}
                onChange={(e) => setLocationDistrict(e.target.value)}
              >
                <option value="">구 선택</option>
                {SEOUL_DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            ) : (
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="구/군"
                value={locationDistrict}
                onChange={(e) => setLocationDistrict(e.target.value)}
              />
            )}
          </div>
          <input
            style={{ ...s.input, marginBottom: 8 }}
            placeholder="동/읍/면 (예: 화곡동)"
            value={locationDong}
            onChange={(e) => setLocationDong(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ ...s.input, flex: 1 }}
              placeholder="경기장 이름 (예: 강서테니스장)"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowAddrModal(true)}
              style={{ padding: "0 16px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              🔍 검색
            </button>
          </div>
        </div>

        {/* 희망 상대 레벨 (다중 선택) */}
        <div style={s.section}>
          <label style={s.label}>희망 상대 레벨 <span style={s.optional}>다중 선택 가능</span></label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {DESIRED_LEVELS.map((lv) => {
              const selected = desiredLevels.includes(lv);
              return (
                <button
                  key={lv}
                  style={{
                    ...s.levelChip,
                    background:  selected ? "#E24B4A" : "#fff",
                    color:       selected ? "#fff"    : "#6b7280",
                    borderColor: selected ? "#E24B4A" : "#e5e7eb",
                  }}
                  onClick={() => {
                    if (lv === "무관") {
                      setDesiredLevels(["무관"]);
                    } else {
                      const without = desiredLevels.filter((v) => v !== "무관");
                      const next = selected ? without.filter((v) => v !== lv) : [...without, lv];
                      setDesiredLevels(next.length === 0 ? ["무관"] : next);
                    }
                  }}
                >
                  {lv}
                </button>
              );
            })}
          </div>
        </div>

        {/* 교류전 희망 참가 인원 */}
        <div style={s.section}>
          <label style={s.label}>
            교류전 희망 참가 인원 <span style={s.optional}>선택</span>
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>👨 남성</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="number" min="0" max="99"
                  style={{ ...s.input, textAlign: "center" }}
                  placeholder="0"
                  value={participantMale}
                  onChange={(e) => setParticipantMale(e.target.value)}
                />
                <span style={{ fontSize: 13, color: "#6b7280", flexShrink: 0 }}>명</span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>👩 여성</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="number" min="0" max="99"
                  style={{ ...s.input, textAlign: "center" }}
                  placeholder="0"
                  value={participantFemale}
                  onChange={(e) => setParticipantFemale(e.target.value)}
                />
                <span style={{ fontSize: 13, color: "#6b7280", flexShrink: 0 }}>명</span>
              </div>
            </div>
          </div>
        </div>

        {/* 모집 메시지 */}
        <div style={s.section}>
          <label style={s.label}>
            모집 메시지 <span style={s.optional}>선택</span>
          </label>
          <textarea
            style={{ ...s.input, resize: "none", height: 90, lineHeight: 1.6 }}
            placeholder="상대 팀에게 전하고 싶은 말을 남겨주세요"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={300}
          />
          <div style={{ textAlign: "right", fontSize: 11, color: "#c4c9d4", marginTop: 4 }}>
            {message.length}/300
          </div>
        </div>

        {/* 모집 마감일 */}
        <div style={s.section}>
          <label style={s.label}>
            모집 마감일 <span style={s.optional}>선택</span>
          </label>
          <input
            type="date"
            style={s.input}
            value={deadline}
            min={isEditMode ? undefined : TODAY}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        <button
          style={{ ...s.submitBtn, opacity: (loading || (!isEditMode && leaderTeams.length >= 2 && !selectedTeam)) ? 0.6 : 1 }}
          onClick={handleSubmit}
          disabled={loading || (!isEditMode && leaderTeams.length >= 2 && !selectedTeam)}
        >
          {loading
            ? (isEditMode ? "수정 중..." : "등록 중...")
            : (isEditMode ? "✅ 수정 완료" : "✅ 모집글 등록하기")}
        </button>
      </div>

      {/* 필수사항 미입력 모달 */}
      {validationModal && (
        <div style={s.backdrop} onClick={() => setValidationModal(null)}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 36, textAlign: "center", marginBottom: 10 }}>⚠️</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", textAlign: "center", marginBottom: 14 }}>
              필수사항을 확인해주세요
            </div>
            <div style={{ marginBottom: 20 }}>
              {validationModal.map((item) => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #f0f1f5" }}>
                  <span style={{ color: "#E24B4A", fontSize: 14 }}>•</span>
                  <span style={{ fontSize: 14, color: "#374151" }}>{item}</span>
                  <span style={{ fontSize: 12, color: "#E24B4A", marginLeft: "auto" }}>미입력</span>
                </div>
              ))}
            </div>
            <button style={s.modalCloseBtn} onClick={() => setValidationModal(null)}>확인</button>
          </div>
        </div>
      )}

      <AddressSearchModal
        open={showAddrModal}
        onClose={() => setShowAddrModal(false)}
        initialQuery={locationName}
        onSelect={(place) => {
          setLocationName(place.name);
          setLocationMapx(place.mapx);
          setLocationMapy(place.mapy);
          setLocationRoadAddr(place.roadAddress || place.address || "");
        }}
      />
    </div>
  );
}

function Header({ onBack, title }) {
  return (
    <div style={s.header}>
      <button style={s.backBtn} onClick={onBack}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        뒤로
      </button>
      <span style={s.headerTitle}>{title}</span>
      <div style={{ width: 64 }} />
    </div>
  );
}

const s = {
  container:   { minHeight: "100vh", background: "#f5f6fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header:      { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(env(safe-area-inset-top) + 12px) 20px 12px", background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" },
  backBtn:     { display: "flex", alignItems: "center", gap: 2, background: "none", border: "none", fontSize: 14, fontWeight: 600, color: "#E24B4A", cursor: "pointer", padding: "4px 0" },
  headerTitle: { fontSize: 16, fontWeight: 700, color: "#111827" },
  body:        { flex: 1, padding: "20px 20px 48px", display: "flex", flexDirection: "column" },
  section:     { marginBottom: 24 },
  sectionTitle:{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 10 },
  label:       { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 },
  required:    { color: "#E24B4A", marginLeft: 2 },
  optional:    { fontSize: 11, color: "#9ca3af", fontWeight: 400, marginLeft: 6 },
  teamBox:     { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10 },
  input:       { width: "100%", padding: "11px 13px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
  levelChip:   { padding: "8px 16px", border: "1px solid #e5e7eb", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" },
  submitBtn:   { width: "100%", padding: "15px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", marginTop: 8 },
  errorBox:    { padding: "12px 14px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 10, fontSize: 13, color: "#dc2626", marginBottom: 16 },
  emptyWrap:   { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", textAlign: "center" },
  backdrop:    { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, backdropFilter: "blur(4px)" },
  modalBox:    { width: "85%", maxWidth: 340, background: "#fff", borderRadius: 18, padding: "24px 22px 26px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)" },
  modalCloseBtn: { width: "100%", padding: "13px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
};
